import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * !IMPORTANT: This hook use "hidden" class to hide items.
 * !You need to add this class to your styles.
 */
export const useTruncate = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [isExtended, setExtend] = useState(false);
  const [hiddenCount, setHiddenCount] = useState<number | null>(null);

  const truncate = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.overflow = "hidden";
    const childNodes = Array.from(ref.current.children) as HTMLElement[];

    if (childNodes.length === 0) return;
    for (const node of childNodes) node.classList.add("hidden");

    for (let i = 0; i < childNodes.length; i++) {
      const item = childNodes[i];
      const nextItem = childNodes[i + 1];

      if (nextItem) {
        item.classList.remove("hidden");
        nextItem.classList.remove("hidden");
        const nextItemRect = nextItem.getBoundingClientRect();
        const containerRect = ref.current.getBoundingClientRect();
        nextItem.classList.add("hidden");

        if (
          nextItemRect.bottom > containerRect.bottom ||
          nextItemRect.right > containerRect.right
        ) {
          item.classList.add("hidden");
          if (i > 0) childNodes[i - 1].classList.remove("hidden");
          break;
        }
      }
    }
    const hiddenItems =
      childNodes.filter((h) => h.classList.contains("hidden")).length - 1;

    if (isExtended || (childNodes.length > hiddenItems && hiddenItems))
      childNodes[childNodes.length - 1].classList.remove("hidden");

    setHiddenCount(hiddenItems);
  }, [isExtended]);

  useLayoutEffect(() => {
    truncate();

    const observer = new ResizeObserver(truncate);

    const copyRef = ref.current;
    if (copyRef) observer.observe(copyRef);

    return () => {
      if (copyRef) observer.unobserve(copyRef);
    };
  }, [truncate, hiddenCount, isExtended]);

  return {
    ref,
    isExtended,
    toggleExtend: () => setExtend((prev) => !prev),
    hiddenCount,
  };
};

// Usage example
// const { ref, isExtended, toggleExtend, hiddenCount } = useTruncate<HTMLUlElement>();
// return (
// <ul
//    ref={ref}
//    style={{
//      maxHeight: isExtended ? "none" : "65px",
//      width: "300px",
//      display: "flex",
//      flexWrap: "wrap",
//    }}
//  >
//    {Array.from({ length: 26} ).map((_, index) => (
//      <li key={index}>item #{index + 1}</li>
//    ))}
//    <li onClick={toggleExtend}>
//      {isExtended ? "Hide" : `+ ${hiddenCount} more...`}
//    </li>
// </ul>
// )
